import React, { useState, useEffect, useCallback } from 'react';
import { programsAPI } from '../api/programs';
import EnhancedProgramCard from './EnhancedProgramCard';
import EffortAmpelFilter from './EffortAmpelFilter';
import { useLang } from '../hooks/useLang';

const ProgramResults = ({ farmData, results, setResults }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [filterLevel, setFilterLevel] = useState('all'); // Ampel Filter State
  const [sortBy, setSortBy] = useState('roi'); // Ampel Sort State
  const { t } = useLang();

  // Handler-Funktion für Karten-Auswahl (Toggle-Verhalten)
  const handleCardSelect = (programId) => {
    setSelectedCardId(prevId => (prevId === programId ? null : programId));
  };

  // Filter und sortiere Programme basierend auf Ampel-Einstellungen
  const filteredAndSortedPrograms = React.useMemo(() => {
    if (!results || results.length === 0) return [];
    
    // 1. Filter nach Ampel-Level
    let filtered = results;
    if (filterLevel !== 'all') {
      filtered = results.filter(program => program.effort?.level === filterLevel);
    }
    
    // 2. Sortiere nach gewähltem Kriterium
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'roi':
          return (b.effort?.roi?.ratio || 0) - (a.effort?.roi?.ratio || 0);
        case 'effort':
          const effortOrder = { green: 1, yellow: 2, orange: 3, red: 4 };
          return (effortOrder[a.effort?.level] || 5) - (effortOrder[b.effort?.level] || 5);
        case 'deadline':
          const aDeadline = new Date(a.deadline || '2099-12-31');
          const bDeadline = new Date(b.deadline || '2099-12-31');
          return aDeadline - bDeadline;
        default:
          return 0;
      }
    });
    
    return sorted;
  }, [results, filterLevel, sortBy]);

  // BESSERER useCallback mit korrekten Dependencies
  const searchPrograms = useCallback(async () => {
    console.log('🚀 searchPrograms started');
    setLoading(true);
    setError(null);
    
    try {
      // Support both farmType and main_branch field names
      const farmType = farmData.farmType || farmData.main_branch;
      const goal = farmData.goal || farmData.main_goal;
      
      if (!farmData || !farmData.country || !farmType) {
        throw new Error('Unvollständige Betriebsdaten für die Suche.');
      }

      const filters = {
        country: farmData.country,
        farmType: farmType,
        goal: goal,
        region: farmData.region,
        ...(farmData.farmSize && { farmSize: parseFloat(farmData.farmSize) }),
        ...(farmData.interests && farmData.interests.length > 0 && { interests: farmData.interests }),
        ...(farmData.minFunding && { minFunding: parseInt(farmData.minFunding) }),
        ...(farmData.age && { age: parseInt(farmData.age) }),
      };

      console.log('🔍 Searching with filters:', filters);

      // Verwende die API-Funktion statt direkten fetch
      const responseData = await programsAPI.filterPrograms(filters);
      console.log('📊 API Response:', responseData);

      // ✅ FINALE, KORRIGIERTE DATENVERARBEITUNG
      if (responseData && responseData.success) {
        // Die korrekte Struktur: responseData.data.programs ist das Array
        const programs = responseData.data?.programs;
        console.log('🔍 Extracted programs:', programs);
        console.log('🔍 Is programs array?', Array.isArray(programs));
        
        if (programs && Array.isArray(programs)) {
          // ✅ VERBESSERTE LOGIK:
          // Überschreibe den State NUR, wenn das neue Ergebnis Programme enthält.
          // Das verhindert, dass ein gutes Ergebnis von einem leeren überschrieben wird.
          if (programs.length > 0) {
            setResults(programs);
            console.log(`✅ State updated with ${programs.length} programs.`);
          } else {
            console.log('🟡 Received empty programs array, but kept existing results to prevent overwrite.');
            setResults([]);
          }
        } else {
          // Dieser Fall sollte nicht eintreten, ist aber eine gute Absicherung.
          console.warn('⚠️ API response successful, but programs is not an array:', responseData);
          setResults([]);
        }
      } else {
        // Fall: { success: false, error: '...' }
        console.error('❌ API returned an error:', responseData);
        setError(responseData.error || 'Ein unbekannter API-Fehler ist aufgetreten.');
        setResults([]);
      }

    } catch (err) {
      console.error('❌ A critical error occurred during fetch:', err);
      setError('Fehler beim Laden der Programme: ' + err.message);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [farmData, setResults]); // NUR farmData und setResults als Dependencies

  useEffect(() => {
    console.log('🔍 ProgramResults useEffect triggered');
    console.log('📊 farmData:', farmData);
    console.log('📋 results:', results);
    
    // NUR suchen wenn:
    // 1. farmData existiert UND vollständig ist
    // 2. results noch nicht existieren
    // 3. nicht bereits am laden
    // Support both farmType and main_branch field names
    const farmType = farmData?.farmType || farmData?.main_branch;
    
    if (farmData && 
        farmData.country && 
        farmType && 
        !results && 
        !loading) {
      
      console.log('✅ Starting search with complete farmData');
      searchPrograms();
    } else {
      console.log('⏳ Waiting for complete farmData or already have results');
    }
  }, [farmData, results, loading, searchPrograms]); // KORREKTE Dependencies

  // LOADING STATE - Verbessert mit Skeleton Loader
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
        <div className="container mx-auto px-4 py-8">
          {/* Loading Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 bg-white rounded-full px-6 py-3 shadow-lg border border-green-100 mb-4">
              <div className="animate-spin w-5 h-5 border-2 border-green-600 border-t-transparent rounded-full"></div>
              <span className="text-lg font-semibold text-gray-800">
                🔍 {t('loading.searching')}
              </span>
            </div>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('loading.analyzing')} • {farmData?.country} • {farmData?.farmType}
            </p>
          </div>

          {/* Skeleton Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 animate-pulse">
                {/* Header Skeleton */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1 pr-4">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="flex gap-2 mb-2">
                      <div className="h-5 bg-gray-200 rounded-full w-16"></div>
                      <div className="h-5 bg-gray-200 rounded-full w-20"></div>
                    </div>
                  </div>
                  <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
                </div>
                
                {/* Description Skeleton */}
                <div className="space-y-2 mb-4">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  <div className="h-4 bg-gray-200 rounded w-4/6"></div>
                </div>
                
                {/* Info Skeleton */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center">
                    <div className="h-4 bg-gray-200 rounded w-20 mr-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </div>
                  <div className="flex items-center">
                    <div className="h-4 bg-gray-200 rounded w-20 mr-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                  </div>
                </div>
                
                {/* Button Skeleton */}
                <div className="pt-4 border-t border-gray-100">
                  <div className="h-12 bg-gray-200 rounded-lg w-full"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ERROR STATE
  if (error) {
    return (
      <div className="results-error">
        <h3>❌ {error}</h3>
        <div className="error-details">
          <p>Mögliche Ursachen:</p>
          <ul>
            <li>Backend-Server ist nicht gestartet</li>
            <li>Datenbankverbindung fehlgeschlagen</li>
            <li>Netzwerkfehler</li>
          </ul>
        </div>
        <button onClick={() => {
          setError(null);
          setResults(null);
          searchPrograms();
        }} className="retry-button">
          🔄 Erneut versuchen
        </button>
        <button onClick={() => {
          setError(null);
          setResults(null);
        }} className="back-button">
          ← Zurück
        </button>
      </div>
    );
  }

  // NO RESULTS STATE
  if (results && results.length === 0) {
    return (
      <div className="no-results">
        <h3>😔 Keine passenden Programme gefunden</h3>
        <p>Für Ihre Suchkriterien wurden keine Förderprogramme gefunden.</p>
        <div className="search-criteria">
          <h4>Ihre Suchkriterien:</h4>
          <ul>
            <li>🌍 Land: {farmData?.country}</li>
            <li>🚜 Betriebsart: {farmData?.farmType}</li>
            <li>📏 Größe: {farmData?.farmSize} ha</li>
            {farmData?.interests && farmData.interests.length > 0 && (
              <li>🏷️ Interessen: {farmData.interests.join(', ')}</li>
            )}
            {farmData?.minFunding && (
              <li>💰 Min. Förderung: €{farmData.minFunding}</li>
            )}
          </ul>
        </div>
        <button onClick={() => {
          setResults(null);
          searchPrograms();
        }} className="retry-button">
          🔄 Erneut suchen
        </button>
      </div>
    );
  }

  // SUCCESS STATE WITH RESULTS
  if (results && results.length > 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 animate-fade-in">
        <div className="container mx-auto px-4 py-8">
          {/* Optimierter Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 bg-white rounded-full px-6 py-3 shadow-lg border border-green-100 mb-4">
              <span className="text-2xl">🎯</span>
              <span className="text-lg font-semibold text-gray-800">
                {results.length} {t('results.programsFound')}
              </span>
            </div>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('results.sortedByRelevance')} • {farmData?.country} • {farmData?.farmType}
            </p>
          </div>

          {/* Top-3 Programme hervorheben */}
          {results.length > 3 && (
            <div className="mb-12">
              <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <span className="text-2xl">🏆</span>
                {t('results.topRecommendations')}
              </h4>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {results.slice(0, 3).map((program, index) => (
                  <EnhancedProgramCard 
                    key={program.id || index}
                    program={program} 
                    index={index}
                    isSelected={selectedCardId === program.id}
                    onSelect={() => handleCardSelect(program.id)}
                    className="transform hover:scale-105 transition-all duration-300 animate-fade-in-up"
                    style={{animationDelay: `${index * 100}ms`}}
                  />
                ))}
              </div>
            </div>
          )}

          {/* 🚦 AMPEL FILTER */}
          <EffortAmpelFilter 
            programs={results}
            filterLevel={filterLevel}
            setFilterLevel={setFilterLevel}
            sortBy={sortBy}
            setSortBy={setSortBy}
          />

          {/* Alle Programme in optimiertem Grid */}
          <div className="mb-8">
            <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <span className="text-2xl">📋</span>
              {filteredAndSortedPrograms.length > 3 ? t('results.allPrograms') : t('results.foundPrograms')}
              <span className="text-sm bg-gray-100 px-2 py-1 rounded-full">
                {filteredAndSortedPrograms.length} {t('card.of')} {results.length}
              </span>
            </h4>
            
            {/* Responsive Grid Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredAndSortedPrograms.map((program, index) => (
                <EnhancedProgramCard 
                  key={program.id || index}
                  program={program} 
                  index={index}
                  isSelected={selectedCardId === program.id}
                  onSelect={() => handleCardSelect(program.id)}
                  className="transform hover:scale-105 transition-all duration-300"
                />
              ))}
            </div>
          </div>

          {/* Statistiken Footer */}
          <div className="mt-12 bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="p-4">
                <div className="text-2xl font-bold text-green-600">{results.length}</div>
                <div className="text-sm text-gray-600">{t('results.foundPrograms')}</div>
              </div>
              <div className="p-4">
                <div className="text-2xl font-bold text-blue-600">
                  {results.filter(p => p.score >= 70).length}
                </div>
                <div className="text-sm text-gray-600">{t('results.highlyRelevant')}</div>
              </div>
              <div className="p-4">
                <div className="text-2xl font-bold text-yellow-600">
                  {results.filter(p => p.deadline && new Date(p.deadline) > new Date()).length}
                </div>
                <div className="text-sm text-gray-600">{t('results.stillAvailable')}</div>
              </div>
              <div className="p-4">
                <div className="text-2xl font-bold text-purple-600">
                  {Math.round(results.reduce((sum, p) => sum + (p.score || 0), 0) / results.length)}
                </div>
                <div className="text-sm text-gray-600">{t('results.avgRelevance')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // INITIAL STATE
  return (
    <div className="results-initial">
      <h3>🔍 Bereit für die Suche...</h3>
      <p>Die Analyse wird automatisch gestartet.</p>
    </div>
  );
};

export default ProgramResults;